import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { BrandCalendarSettings } from "@/types/contentCalendar.type";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/utils/helpers";
import { clientTitle } from "../postState";
import { useStuckClock } from "../useStuckClock";
import { isCurrentMonth, isMonthInPast } from "../calendar.utils";
import { autoPlanRun, pillarIndexMap, readPosts, todayUtc, type PillarRef } from "../month/calendarModel";
import { useCalendarViewport } from "../month/CalendarSheet";
import YearMonth, { YearDot } from "./YearMonth";
import { calendarState, monthPath, unplannedRow, type CalendarRow } from "./calendarIndex";

/** A date cell's height on the desktop year: shrinks to fit the screen, never
 *  past the floor, so a short screen scrolls rather than crushing the dates. */
const CELL_MIN = 23;
const CELL_MAX = 30;
/** Six weeks a month, three rows of months. */
const CELLS_TALL = 18;

export interface YearWindowStart {
  year: number;
  /** 1-12. */
  month: number;
}

/** The twelve months from `start`, as "2026-07" keys. */
export function windowKeys(start: YearWindowStart): string[] {
  return Array.from({ length: 12 }, (_, index) => {
    const at = start.year * 12 + (start.month - 1) + index;
    return `${Math.floor(at / 12)}-${String((at % 12) + 1).padStart(2, "0")}`;
  });
}

/**
 * The twelve cells of the window. The list endpoint returns the months that
 * EXIST, so every empty cell gets the same `unplannedRow`: a month with no plan
 * and a month outside the planning window read identically.
 */
export function monthsForWindow(start: YearWindowStart, rows: CalendarRow[]): CalendarRow[] {
  const byKey = new Map(rows.map((row) => [`${row.year}-${String(row.month).padStart(2, "0")}`, row]));
  return windowKeys(start).map((key) => byKey.get(key) ?? unplannedRow(Number(key.slice(0, 4)), Number(key.slice(5, 7))));
}

/**
 * The year (Pen: Year · twelve calendars): twelve Calendar / Year month minis,
 * four across, 44 apart and 22 between rows, then Calendar / Year legend along
 * the foot. Rows carry their posts once each month has been read; until then a
 * month shows its counts without dots.
 */
export default function CalendarYearGrid({
  start,
  rows,
  settings,
  onOpenMonth,
  onPlan,
  pillars,
  clientName = null,
}: {
  start: YearWindowStart;
  /** Every calendar this brand has. The window is cut from it here. */
  rows: CalendarRow[];
  settings?: BrandCalendarSettings | null;
  onOpenMonth?: ((row: CalendarRow) => void) | null;
  onPlan?: ((row: CalendarRow) => void) | null;
  pillars?: PillarRef[];
  clientName?: string | null;
}) {
  const navigate = useNavigate();
  const { isGuest } = usePermissions();
  const viewport = useCalendarViewport();
  const now = useStuckClock(false);
  const today = todayUtc(now);
  const months = monthsForWindow(start, rows);
  const pillarIndex = useMemo(
    () => pillarIndexMap(pillars ?? [], rows.flatMap((row) => row.slots ?? []).map((slot) => slot.pillarId)),
    [pillars, rows],
  );
  const phone = viewport === "phone";
  const desktop = viewport === "desktop";

  /*
    FIT THE YEAR TO THE SCREEN. Everything above and below the dates is fixed,
    so the page's height moves 18 px for every pixel a cell grows: measure how
    far the year overshoots or falls short of the viewport and spread that over
    the cells. One step lands, so a resize settles without a loop.
  */
  const sectionRef = useRef<HTMLElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const [cell, setCell] = useState(CELL_MAX);
  useLayoutEffect(() => {
    if (!desktop) return;
    const fit = () => {
      const section = sectionRef.current;
      const legend = legendRef.current;
      const last = section?.lastElementChild;
      if (!section || !legend || !last) return;
      const page = section.closest(".co-cal-page") ?? section.parentElement!;
      const used =
        last.getBoundingClientRect().bottom -
        page.getBoundingClientRect().top +
        legend.getBoundingClientRect().height +
        parseFloat(getComputedStyle(legend).marginTop);
      setCell((current) =>
        Math.max(CELL_MIN, Math.min(CELL_MAX, Math.floor(current + (window.innerHeight - used) / CELLS_TALL))),
      );
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [desktop, rows]);

  return (
    <div className="flex flex-1 flex-col">
      <section
        ref={sectionRef}
        aria-label="Twelve months"
        className={cn(
          "grid flex-1 content-start",
          // A phone reads the year two minis across (Year month · Phone); a tablet
          // four, like the desk, in 146-wide minis (Figma 08 Tablet · Year, 13 Oct).
          phone
            ? "grid-cols-2 gap-x-4 gap-y-7"
            : viewport === "tablet"
              ? "grid-cols-4 gap-x-11 gap-y-[22px] px-3"
              : "grid-cols-4 gap-x-10 gap-y-4 px-8 pt-1",
        )}
      >
        {months.map((row, index) => {
          const posts = row.slots
            ? readPosts(row.slots, { briefBySlot: row.briefBySlot, clientName, isGuest, now, pillarIndex })
            : null;
          const planned = !!row.planStatus && calendarState(row) !== "failed";
          const run = autoPlanRun(settings, row.year, row.month, now);
          return (
            <YearMonth
              key={`${row.year}-${row.month}`}
              row={row}
              posts={posts}
              todayUtc={today}
              isPast={isMonthInPast(row.year, row.month)}
              isCurrent={isCurrentMonth(row)}
              showYear={index > 0 && row.month === 1}
              autoPlan={run?.label ?? null}
              guest={isGuest}
              compact={phone}
              tablet={viewport === "tablet"}
              cellSize={desktop ? cell : undefined}
              onOpen={planned ? () => (onOpenMonth ? onOpenMonth(row) : navigate(monthPath(row))) : null}
              onPlan={onPlan ? () => onPlan(row) : null}
            />
          );
        })}
      </section>

      {/* Calendar / Year legend: the dots' key along the foot. */}
      <div
        ref={legendRef}
        className={cn(
          "flex flex-wrap items-center gap-x-[18px] gap-y-1 text-[12px] text-co-text2 shadow-[inset_0_1px_0_var(--co-line)]",
          phone ? "mt-6 pt-3" : desktop ? "mt-3 px-8 pb-3 pt-2.5" : "mt-6 px-8 pb-4 pt-3.5",
        )}
      >
        <LegendItem kind="you">Your turn</LegendItem>
        <LegendItem kind="client">{isGuest ? "The team" : clientTitle(clientName)}&rsquo;s turn</LegendItem>
        <LegendItem kind="moving">Moving or done</LegendItem>
        <LegendItem kind="live">Live</LegendItem>
        <LegendItem kind="missed">Missed</LegendItem>
        <span className="flex-1" />
        <span className="text-co-muted">Pick a month to open it</span>
      </div>
    </div>
  );
}

function LegendItem({ kind, children }: { kind: Parameters<typeof YearDot>[0]["kind"]; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <YearDot kind={kind} size={7} />
      {children}
    </span>
  );
}
